
import React, { useMemo, useState } from 'react';
import { LayoutGrid, Trophy } from 'lucide-react';
import { StandingItem, Game, Language } from '../../types';
import { ImageWithFallback } from '../Common/ImageWithFallback';
import { getTeamLogo } from '../../services/footballService';
import { getTeamName } from '../../services/teamService';
import { getFifaRanking } from '../../services/fifaRankingService';

interface Props {
    standings: StandingItem[];
    fixtures: Game[];
    selectedGroup: string | null;
    lang: Language;
}

interface TeamScenario {
    teamId: string;
    firstCount: number;
    secondCount: number;
    thirdCount: number;
    fourthCount: number;
    totalScenarios: number;
    maxPoints: number;
}

interface ThirdPlaceEntry {
    team: StandingItem;
    groupLetter: string;
    isComplete: boolean;
    remainingCount: number;
    wildcardRank: number;
}

// H2H 매트릭스: h2h[teamId][opponentId] = { gf: 해당 팀 득점, ga: 상대 득점 }
type H2HMatrix = Record<string, Record<string, { gf: number; ga: number }>>;

// 동률 팀 간 승자승 계산
function computeH2H(
    teamIds: string[],
    h2hMatrix: H2HMatrix
): Record<string, { pts: number; gd: number; gf: number }> {
    const result: Record<string, { pts: number; gd: number; gf: number }> = {};
    teamIds.forEach(id => {
        let pts = 0, gf = 0, ga = 0;
        teamIds.forEach(opp => {
            if (opp === id) return;
            const r = h2hMatrix[id]?.[opp];
            if (!r) return;
            gf += r.gf; ga += r.ga;
            if (r.gf > r.ga) pts += 3;
            else if (r.gf === r.ga) pts += 1;
        });
        result[id] = { pts, gd: gf - ga, gf };
    });
    return result;
}

// FIFA 월드컵 조별리그 순위 결정: 승점 → 골득실 → 다득점 → 승자승
function sortTeamsFifa(
    teamIds: string[],
    overall: Record<string, { pts: number; gd: number; gf: number }>,
    h2hMatrix: H2HMatrix
): string[] {
    const sorted = [...teamIds].sort((a, b) => {
        const oa = overall[a], ob = overall[b];
        if (ob.pts !== oa.pts) return ob.pts - oa.pts;
        if (ob.gd !== oa.gd) return ob.gd - oa.gd;
        return ob.gf - oa.gf;
    });

    const result: string[] = [];
    let i = 0;
    while (i < sorted.length) {
        const o0 = overall[sorted[i]];
        let j = i + 1;
        while (j < sorted.length) {
            const oj = overall[sorted[j]];
            if (oj.pts === o0.pts && oj.gd === o0.gd && oj.gf === o0.gf) j++;
            else break;
        }

        if (j - i === 1) {
            result.push(sorted[i]);
        } else {
            // 동률 시 승자승(H2H) 적용
            const tiedIds = sorted.slice(i, j);
            const h2h = computeH2H(tiedIds, h2hMatrix);
            const h2hSorted = [...tiedIds].sort((a, b) => {
                if (h2h[b].pts !== h2h[a].pts) return h2h[b].pts - h2h[a].pts;
                if (h2h[b].gd !== h2h[a].gd) return h2h[b].gd - h2h[a].gd;
                return h2h[b].gf - h2h[a].gf;
            });
            result.push(...h2hSorted);
        }
        i = j;
    }

    return result;
}

function calculateGroupScenarios(
    groupTeams: StandingItem[],
    remainingMatches: Array<{ homeTeamId: string; awayTeamId: string }>,
    finishedH2H: H2HMatrix
): Record<string, TeamScenario> {
    const n = remainingMatches.length;
    const totalScenarios = n > 0 ? Math.pow(3, n) : 1;

    const teamData: Record<string, TeamScenario> = {};
    groupTeams.forEach(t => {
        const teamRemaining = remainingMatches.filter(
            m => m.homeTeamId === t.team.id || m.awayTeamId === t.team.id
        ).length;
        teamData[t.team.id] = {
            teamId: t.team.id,
            firstCount: 0,
            secondCount: 0,
            thirdCount: 0,
            fourthCount: 0,
            totalScenarios,
            maxPoints: t.points + 3 * teamRemaining,
        };
    });

    // 모든 경기 완료 시: 승자승·페어플레이까지 반영된 API rank를 신뢰하여 직접 사용
    if (n === 0) {
        groupTeams.forEach(t => {
            if (t.rank === 1) teamData[t.team.id].firstCount = 1;
            else if (t.rank === 2) teamData[t.team.id].secondCount = 1;
            else if (t.rank === 3) teamData[t.team.id].thirdCount = 1;
            else teamData[t.team.id].fourthCount = 1;
        });
        return teamData;
    }

    for (let scenario = 0; scenario < totalScenarios; scenario++) {
        const overall: Record<string, { pts: number; gd: number; gf: number }> = {};
        groupTeams.forEach(t => {
            overall[t.team.id] = { pts: t.points, gd: t.goalDifference, gf: t.goalsFor };
        });

        // 완료된 경기의 H2H 결과를 기반으로 매트릭스 초기화
        const h2h: H2HMatrix = {};
        groupTeams.forEach(t => {
            h2h[t.team.id] = {};
            Object.entries(finishedH2H[t.team.id] || {}).forEach(([opp, r]) => {
                h2h[t.team.id][opp] = { ...r };
            });
        });

        let temp = scenario;
        for (let i = 0; i < n; i++) {
            const outcome = temp % 3;
            temp = Math.floor(temp / 3);
            const { homeTeamId, awayTeamId } = remainingMatches[i];

            if (!h2h[homeTeamId]) h2h[homeTeamId] = {};
            if (!h2h[awayTeamId]) h2h[awayTeamId] = {};

            if (outcome === 0) { // 홈 승
                overall[homeTeamId].pts += 3;
                overall[homeTeamId].gd += 1;
                overall[homeTeamId].gf += 1;
                overall[awayTeamId].gd -= 1;
                h2h[homeTeamId][awayTeamId] = { gf: 1, ga: 0 };
                h2h[awayTeamId][homeTeamId] = { gf: 0, ga: 1 };
            } else if (outcome === 1) { // 무승부
                overall[homeTeamId].pts += 1;
                overall[awayTeamId].pts += 1;
                h2h[homeTeamId][awayTeamId] = { gf: 0, ga: 0 };
                h2h[awayTeamId][homeTeamId] = { gf: 0, ga: 0 };
            } else { // 원정 승
                overall[awayTeamId].pts += 3;
                overall[awayTeamId].gd += 1;
                overall[awayTeamId].gf += 1;
                overall[homeTeamId].gd -= 1;
                h2h[homeTeamId][awayTeamId] = { gf: 0, ga: 1 };
                h2h[awayTeamId][homeTeamId] = { gf: 1, ga: 0 };
            }
        }

        const teamIds = groupTeams.map(t => t.team.id);
        const sorted = sortTeamsFifa(teamIds, overall, h2h);

        sorted.forEach((id, rank) => {
            if (rank === 0) teamData[id].firstCount++;
            else if (rank === 1) teamData[id].secondCount++;
            else if (rank === 2) teamData[id].thirdCount++;
            else teamData[id].fourthCount++;
        });
    }

    return teamData;
}

// 3위 팀 분포 타입: 승점 + 골득실 + 다득점까지 포함 (와일드카드 타이브레이커 반영)
type ThirdPlaceDist = Array<{ points: number; gd: number; gf: number; prob: number }>;

// 조의 3위 팀이 가질 수 있는 (승점, 골득실, 다득점) 분포
function getGroupThirdPlacePointsDist(
    groupTeams: StandingItem[],
    remainingMatches: Array<{ homeTeamId: string; awayTeamId: string }>,
    finishedH2H: H2HMatrix
): ThirdPlaceDist {
    const n = remainingMatches.length;
    const total = n > 0 ? Math.pow(3, n) : 1;
    const countMap: Record<string, number> = {};
    const teamIds = groupTeams.map(t => t.team.id);

    for (let s = 0; s < total; s++) {
        const overall: Record<string, { pts: number; gd: number; gf: number }> = {};
        groupTeams.forEach(t => { overall[t.team.id] = { pts: t.points, gd: t.goalDifference, gf: t.goalsFor }; });

        const h2h: H2HMatrix = {};
        groupTeams.forEach(t => {
            h2h[t.team.id] = {};
            Object.entries(finishedH2H[t.team.id] || {}).forEach(([opp, r]) => { h2h[t.team.id][opp] = { ...r }; });
        });

        let temp = s;
        for (let i = 0; i < n; i++) {
            const o = temp % 3; temp = Math.floor(temp / 3);
            const { homeTeamId: h, awayTeamId: a } = remainingMatches[i];
            if (!h2h[h]) h2h[h] = {};
            if (!h2h[a]) h2h[a] = {};
            if (o === 0) { overall[h].pts += 3; overall[h].gd += 1; overall[h].gf += 1; overall[a].gd -= 1; h2h[h][a] = { gf: 1, ga: 0 }; h2h[a][h] = { gf: 0, ga: 1 }; }
            else if (o === 1) { overall[h].pts += 1; overall[a].pts += 1; h2h[h][a] = { gf: 0, ga: 0 }; h2h[a][h] = { gf: 0, ga: 0 }; }
            else { overall[a].pts += 3; overall[a].gd += 1; overall[a].gf += 1; overall[h].gd -= 1; h2h[h][a] = { gf: 0, ga: 1 }; h2h[a][h] = { gf: 1, ga: 0 }; }
        }

        const sorted = sortTeamsFifa(teamIds, overall, h2h);
        const thirdId = sorted[2];
        if (thirdId) {
            const { pts, gd, gf } = overall[thirdId];
            const key = `${pts}:${gd}:${gf}`;
            countMap[key] = (countMap[key] || 0) + 1;
        }
    }
    return Object.entries(countMap).map(([key, count]) => {
        const [pts, gd, gf] = key.split(':').map(Number);
        return { points: pts, gd, gf, prob: count / total };
    });
}

// 특정 팀이 3위가 될 때 가질 수 있는 (승점, 골득실, 다득점) 분포
function getTeamThirdPlacePointsDist(
    teamId: string,
    groupTeams: StandingItem[],
    remainingMatches: Array<{ homeTeamId: string; awayTeamId: string }>,
    finishedH2H: H2HMatrix
): ThirdPlaceDist {
    const n = remainingMatches.length;
    const total = n > 0 ? Math.pow(3, n) : 1;
    const countMap: Record<string, number> = {};
    const teamIds = groupTeams.map(t => t.team.id);

    for (let s = 0; s < total; s++) {
        const overall: Record<string, { pts: number; gd: number; gf: number }> = {};
        groupTeams.forEach(t => { overall[t.team.id] = { pts: t.points, gd: t.goalDifference, gf: t.goalsFor }; });

        const h2h: H2HMatrix = {};
        groupTeams.forEach(t => {
            h2h[t.team.id] = {};
            Object.entries(finishedH2H[t.team.id] || {}).forEach(([opp, r]) => { h2h[t.team.id][opp] = { ...r }; });
        });

        let temp = s;
        for (let i = 0; i < n; i++) {
            const o = temp % 3; temp = Math.floor(temp / 3);
            const { homeTeamId: h, awayTeamId: a } = remainingMatches[i];
            if (!h2h[h]) h2h[h] = {};
            if (!h2h[a]) h2h[a] = {};
            if (o === 0) { overall[h].pts += 3; overall[h].gd += 1; overall[h].gf += 1; overall[a].gd -= 1; h2h[h][a] = { gf: 1, ga: 0 }; h2h[a][h] = { gf: 0, ga: 1 }; }
            else if (o === 1) { overall[h].pts += 1; overall[a].pts += 1; h2h[h][a] = { gf: 0, ga: 0 }; h2h[a][h] = { gf: 0, ga: 0 }; }
            else { overall[a].pts += 3; overall[a].gd += 1; overall[a].gf += 1; overall[h].gd -= 1; h2h[h][a] = { gf: 0, ga: 1 }; h2h[a][h] = { gf: 1, ga: 0 }; }
        }

        const sorted = sortTeamsFifa(teamIds, overall, h2h);
        if (sorted[2] === teamId) {
            const { pts, gd, gf } = overall[teamId];
            const key = `${pts}:${gd}:${gf}`;
            countMap[key] = (countMap[key] || 0) + 1;
        }
    }
    return Object.entries(countMap).map(([key, count]) => {
        const [pts, gd, gf] = key.split(':').map(Number);
        return { points: pts, gd, gf, prob: count / total };
    });
}

// 와일드카드 진출 확률: 12개 조 3위 중 상위 8팀 진출 기준
// "더 높다" = 승점 우위 OR 동점+골득실 우위 OR 동점+동득실+다득점 우위
function computeWildcardQualifyProb(
    teamThirdDist: ThirdPlaceDist,
    otherGroupDists: ThirdPlaceDist[]
): number {
    const numOthers = otherGroupDists.length;
    let result = 0;
    for (const { points: p, gd: myGd, gf: myGf, prob: qT } of teamThirdDist) {
        if (qT <= 0) continue;
        let dp: number[] = new Array(numOthers + 1).fill(0);
        dp[0] = 1;
        for (const dist of otherGroupDists) {
            const newDp: number[] = new Array(numOthers + 1).fill(0);
            const pAbove = dist.reduce((s, d) => {
                const above = d.points > p ||
                    (d.points === p && d.gd > myGd) ||
                    (d.points === p && d.gd === myGd && d.gf > myGf);
                return above ? s + d.prob : s;
            }, 0);
            for (let j = 0; j <= numOthers; j++) {
                if (!dp[j]) continue;
                newDp[j] += dp[j] * (1 - pAbove);
                if (j + 1 <= numOthers) newDp[j + 1] += dp[j] * pAbove;
            }
            dp = newDp;
        }
        let pQ = 0;
        for (let j = 0; j <= Math.min(7, numOthers); j++) pQ += dp[j];
        result += qT * pQ;
    }
    return result;
}

const formatFifaRank = (rank: number, lang: Language): string => {
    switch (lang) {
        case 'ko': return `FIFA ${rank}위`;
        case 'ja': return `FIFA ${rank}位`;
        case 'es': return `FIFA ${rank}°`;
        case 'fr': return `FIFA ${rank}e`;
        default: {
            const v = rank % 100;
            const suffix = v >= 11 && v <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][rank % 10] ?? 'th';
            return `FIFA ${rank}${suffix}`;
        }
    }
};

export const WorldCupScenarios: React.FC<Props> = ({ standings, fixtures, selectedGroup, lang }) => {
    const [viewMode, setViewMode] = useState<'all' | 'third'>('all');
    const isKo = lang === 'ko';

    const groups = useMemo(() => {
        const g: Record<string, StandingItem[]> = {};
        standings.forEach(team => {
            const groupName = (team.group || 'Group').trim();
            if (!g[groupName]) g[groupName] = [];
            g[groupName].push(team);
        });
        return g;
    }, [standings]);

    const teamGroupMap = useMemo(() => {
        const map: Record<string, string> = {};
        Object.entries(groups).forEach(([groupName, teams]) => {
            teams.forEach(t => { map[t.team.id] = groupName; });
        });
        return map;
    }, [groups]);

    const groupFixtures = useMemo(() => {
        const gf: Record<string, Array<{ homeTeamId: string; awayTeamId: string }>> = {};
        fixtures.forEach(f => {
            if (f.status === 'Finished') return;
            const homeId = f.homeTeam.id;
            const awayId = f.awayTeam.id;
            if (!homeId || !awayId) return;
            const homeGroup = teamGroupMap[homeId];
            const awayGroup = teamGroupMap[awayId];
            if (homeGroup && homeGroup === awayGroup) {
                if (!gf[homeGroup]) gf[homeGroup] = [];
                gf[homeGroup].push({ homeTeamId: homeId, awayTeamId: awayId });
            }
        });
        return gf;
    }, [fixtures, teamGroupMap]);

    // 완료된 조별 경기 결과를 파싱해 H2H 매트릭스 구성 (score 형식: "home:away")
    const finishedGroupH2H = useMemo(() => {
        const result: Record<string, H2HMatrix> = {};
        fixtures.forEach(f => {
            if (f.status !== 'Finished' || !f.score) return;
            const homeId = f.homeTeam.id;
            const awayId = f.awayTeam.id;
            if (!homeId || !awayId) return;
            const homeGroup = teamGroupMap[homeId];
            const awayGroup = teamGroupMap[awayId];
            if (!homeGroup || homeGroup !== awayGroup) return;
            const [homeStr, awayStr] = f.score.split(':');
            const homeGoals = parseInt(homeStr, 10);
            const awayGoals = parseInt(awayStr, 10);
            if (isNaN(homeGoals) || isNaN(awayGoals)) return;
            if (!result[homeGroup]) result[homeGroup] = {};
            if (!result[homeGroup][homeId]) result[homeGroup][homeId] = {};
            if (!result[homeGroup][awayId]) result[homeGroup][awayId] = {};
            result[homeGroup][homeId][awayId] = { gf: homeGoals, ga: awayGoals };
            result[homeGroup][awayId][homeId] = { gf: awayGoals, ga: homeGoals };
        });
        return result;
    }, [fixtures, teamGroupMap]);

    const groupScenarios = useMemo(() => {
        const gs: Record<string, Record<string, TeamScenario>> = {};
        Object.entries(groups).forEach(([groupName, teams]) => {
            gs[groupName] = calculateGroupScenarios(
                teams,
                groupFixtures[groupName] || [],
                finishedGroupH2H[groupName] || {}
            );
        });
        return gs;
    }, [groups, groupFixtures, finishedGroupH2H]);

    const groupThirdPlaceDists = useMemo(() => {
        const dists: Record<string, ThirdPlaceDist> = {};
        Object.entries(groups).forEach(([groupName, teams]) => {
            const remaining = groupFixtures[groupName] || [];
            if (remaining.length === 0) {
                // 완료 그룹: API rank=3인 팀의 승점+골득실+다득점을 확정값으로 사용
                const thirdTeam = teams.find(t => t.rank === 3);
                dists[groupName] = thirdTeam
                    ? [{ points: thirdTeam.points, gd: thirdTeam.goalDifference, gf: thirdTeam.goalsFor, prob: 1.0 }]
                    : [];
            } else {
                dists[groupName] = getGroupThirdPlacePointsDist(
                    teams,
                    remaining,
                    finishedGroupH2H[groupName] || {}
                );
            }
        });
        return dists;
    }, [groups, groupFixtures, finishedGroupH2H]);

    // 각 팀이 3위로 와일드카드를 통해 진출할 확률 (P(3위 AND 와일드카드))
    const wildcardQualifyProbs = useMemo(() => {
        const probs: Record<string, number> = {};
        Object.entries(groups).forEach(([groupName, teams]) => {
            const remaining = groupFixtures[groupName] || [];
            const otherDists = Object.entries(groupThirdPlaceDists)
                .filter(([g]) => g !== groupName)
                .map(([, d]) => d);
            teams.forEach(team => {
                const sc = groupScenarios[groupName]?.[team.team.id];
                if (!sc || sc.thirdCount === 0) { probs[team.team.id] = 0; return; }

                let teamThirdDist: ThirdPlaceDist;
                if (remaining.length === 0) {
                    // 완료 그룹: 3위 확정이므로 현재 승점+골득실+다득점 확정값 사용
                    teamThirdDist = [{ points: team.points, gd: team.goalDifference, gf: team.goalsFor, prob: 1.0 }];
                } else {
                    teamThirdDist = getTeamThirdPlacePointsDist(
                        team.team.id,
                        teams,
                        remaining,
                        finishedGroupH2H[groupName] || {}
                    );
                }
                probs[team.team.id] = computeWildcardQualifyProb(teamThirdDist, otherDists);
            });
        });
        return probs;
    }, [groups, groupFixtures, finishedGroupH2H, groupScenarios, groupThirdPlaceDists]);

    const thirdPlaceTeams = useMemo((): ThirdPlaceEntry[] => {
        const entries: ThirdPlaceEntry[] = [];
        Object.entries(groups).forEach(([groupName, teams]) => {
            const remaining = groupFixtures[groupName] || [];
            const sorted = [...teams].sort((a, b) => {
                if (b.points !== a.points) return b.points - a.points;
                if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
                return b.goalsFor - a.goalsFor;
            });
            const thirdTeam = sorted[2];
            if (thirdTeam) {
                entries.push({
                    team: thirdTeam,
                    groupLetter: groupName.replace('Group ', ''),
                    isComplete: remaining.length === 0,
                    remainingCount: remaining.length,
                    wildcardRank: 0,
                });
            }
        });

        entries.sort((a, b) => {
            if (b.team.points !== a.team.points) return b.team.points - a.team.points;
            if (b.team.goalDifference !== a.team.goalDifference) return b.team.goalDifference - a.team.goalDifference;
            return b.team.goalsFor - a.team.goalsFor;
        });
        entries.forEach((e, idx) => { e.wildcardRank = idx + 1; });

        return entries;
    }, [groups, groupFixtures]);

    const sortedGroups = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    const displayGroups = (selectedGroup && viewMode === 'all')
        ? sortedGroups.filter(([g]) => g === selectedGroup)
        : sortedGroups;

    if (standings.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-32 text-gray-300">
                <div className="w-8 h-8 border-4 border-gray-100 border-t-rose-400 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div>
            {/* Toggle: 전체 / 3위 경쟁 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-white sticky top-0 z-10">
                <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    {viewMode === 'all'
                        ? (isKo ? '전체 그룹' : 'All Groups')
                        : (isKo ? '3위 경쟁' : '3rd Place Race')}
                </span>
                <div className="flex items-center bg-gray-100 rounded-full p-1 gap-0.5">
                    <button
                        onClick={() => setViewMode('all')}
                        title={isKo ? '전체' : 'All Groups'}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            viewMode === 'all'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-gray-400 hover:text-gray-600'
                        }`}
                    >
                        <LayoutGrid size={14} strokeWidth={2.5} />
                    </button>
                    <button
                        onClick={() => setViewMode('third')}
                        title={isKo ? '3위 경쟁' : '3rd Place'}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                            viewMode === 'third'
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-gray-400 hover:text-gray-600'
                        }`}
                    >
                        <Trophy size={14} strokeWidth={2.5} />
                    </button>
                </div>
            </div>

            {viewMode === 'all' ? (
                /* ──────── 전체 그룹 시나리오 뷰 ──────── */
                <div className="p-4 space-y-4 pb-10">
                    {displayGroups.map(([groupName, teams]) => {
                        const scenarios = groupScenarios[groupName] || {};
                        const remaining = groupFixtures[groupName] || [];
                        const totalScenarios = remaining.length > 0 ? Math.pow(3, remaining.length) : 1;
                        const allDone = remaining.length === 0;

                        // allDone 시에는 H2H가 반영된 시나리오 결과(1위/2위/3위/4위 count)로 정렬
                        // 진행 중에는 현재 승점/득실/다득점 기준 정렬
                        const sortedTeams = allDone
                            ? [...teams].sort((a, b) => {
                                const rankOf = (t: StandingItem) => {
                                    const s = scenarios[t.team.id];
                                    if (!s) return 4;
                                    if (s.firstCount > 0) return 1;
                                    if (s.secondCount > 0) return 2;
                                    if (s.thirdCount > 0) return 3;
                                    return 4;
                                };
                                return rankOf(a) - rankOf(b);
                            })
                            : [...teams].sort((a, b) => {
                                if (b.points !== a.points) return b.points - a.points;
                                if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
                                return b.goalsFor - a.goalsFor;
                            });

                        return (
                            <div key={groupName} className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                                <div className="px-4 pt-4 pb-3 border-b border-gray-50 flex items-center justify-between">
                                    <span className="text-[11px] font-black text-slate-600 uppercase tracking-widest">{groupName}</span>
                                    <span className="text-[9px] text-gray-400 font-medium">
                                        {allDone
                                            ? (isKo ? '모든 경기 종료' : 'All matches completed')
                                            : (isKo
                                                ? `잔여 ${remaining.length}경기 · ${totalScenarios}가지 경우의 수`
                                                : `${remaining.length} left · ${totalScenarios} scenarios`)}
                                    </span>
                                </div>

                                <div className="divide-y divide-gray-50/80">
                                    {sortedTeams.map((team) => {
                                        const sc = scenarios[team.team.id];
                                        if (!sc) return null;

                                        const qualifyCount = sc.firstCount + sc.secondCount;
                                        const scenarioPct = sc.totalScenarios > 0
                                            ? (qualifyCount / sc.totalScenarios) * 100
                                            : 0;

                                        let dotColor = 'bg-gray-300';
                                        let statusText = '';
                                        let statusTextColor = 'text-gray-400';
                                        let barPct = scenarioPct;
                                        let barColor = 'bg-gray-200';

                                        const wcRawProb = wildcardQualifyProbs[team.team.id] ?? 0;
                                        const wcPct = Math.round(wcRawProb * 100);

                                        // allDone 시 실제 순위는 H2H를 적용한 시나리오 카운트로 판단
                                        const actualRank = allDone
                                            ? (sc.firstCount > 0 ? 1 : sc.secondCount > 0 ? 2 : sc.thirdCount > 0 ? 3 : 4)
                                            : null;

                                        if (allDone) {
                                            if (actualRank! <= 2) {
                                                dotColor = 'bg-emerald-500';
                                                statusText = isKo ? `진출 확정 (${actualRank}위)` : `Qualified (${actualRank}${actualRank === 1 ? 'st' : 'nd'})`;
                                                statusTextColor = 'text-emerald-600';
                                                barPct = 100;
                                                barColor = 'bg-emerald-500';
                                            } else if (actualRank === 3) {
                                                if (wcPct >= 99) {
                                                    dotColor = 'bg-emerald-500';
                                                    statusText = isKo ? '3위 와일드카드 진출 확정' : '3rd Wildcard Guaranteed';
                                                    statusTextColor = 'text-emerald-600';
                                                    barPct = 100;
                                                    barColor = 'bg-emerald-500';
                                                } else if (wcPct <= 1) {
                                                    dotColor = 'bg-red-400';
                                                    statusText = isKo ? '3위 와일드카드 탈락' : '3rd Place — Wildcard Elim.';
                                                    statusTextColor = 'text-red-500';
                                                    barPct = 0;
                                                    barColor = 'bg-red-200';
                                                } else {
                                                    dotColor = 'bg-amber-400';
                                                    statusText = isKo ? `3위 와일드카드 ${wcPct}%` : `3rd Wildcard ${wcPct}%`;
                                                    statusTextColor = 'text-amber-600';
                                                    barPct = wcPct;
                                                    barColor = wcPct >= 66 ? 'bg-amber-400' : wcPct >= 33 ? 'bg-amber-300' : 'bg-amber-200';
                                                }
                                            } else {
                                                dotColor = 'bg-red-400';
                                                statusText = isKo ? '탈락 확정 (4위)' : 'Eliminated (4th)';
                                                statusTextColor = 'text-red-500';
                                                barPct = 0;
                                                barColor = 'bg-red-200';
                                            }
                                        } else {
                                            // 총 진출 확률 = 1·2위 확률 + 3위 와일드카드 확률
                                            const totalPct = scenarioPct + wcRawProb * 100;
                                            barPct = totalPct;

                                            if (totalPct >= 99.9) {
                                                dotColor = 'bg-emerald-500';
                                                statusText = isKo ? '진출 확정' : 'Guaranteed';
                                                statusTextColor = 'text-emerald-600';
                                                barColor = 'bg-emerald-500';
                                            } else if (totalPct <= 0.1) {
                                                dotColor = 'bg-red-400';
                                                statusText = isKo ? '탈락 확정' : 'Eliminated';
                                                statusTextColor = 'text-red-500';
                                                barPct = 0;
                                                barColor = 'bg-red-200';
                                            } else if (scenarioPct <= 0.1) {
                                                // 1·2위 진출 불가, 와일드카드 경로만 존재
                                                const wp = Math.round(totalPct);
                                                dotColor = 'bg-amber-400';
                                                statusText = isKo ? `3위 와일드카드 ${wp}%` : `Wildcard ${wp}%`;
                                                statusTextColor = 'text-amber-600';
                                                barColor = totalPct >= 66 ? 'bg-amber-400' : totalPct >= 33 ? 'bg-amber-300' : 'bg-amber-200';
                                            } else {
                                                const tp = Math.round(totalPct);
                                                const sp = Math.round(scenarioPct);
                                                // 와일드카드 기여가 1% 이상이면 총 확률로 표기
                                                const label = wcRawProb * 100 >= 1
                                                    ? (isKo ? `32강 진출 ${tp}%` : `Qualify ${tp}%`)
                                                    : (isKo ? `${qualifyCount}/${sc.totalScenarios} 경우 진출 (${sp}%)` : `${qualifyCount}/${sc.totalScenarios} qualify (${sp}%)`);
                                                if (totalPct >= 66) {
                                                    dotColor = 'bg-emerald-400';
                                                    statusText = label;
                                                    statusTextColor = 'text-emerald-600';
                                                    barColor = 'bg-emerald-400';
                                                } else if (totalPct > 33) {
                                                    dotColor = 'bg-amber-400';
                                                    statusText = label;
                                                    statusTextColor = 'text-amber-600';
                                                    barColor = 'bg-amber-400';
                                                } else {
                                                    dotColor = 'bg-red-400';
                                                    statusText = label;
                                                    statusTextColor = 'text-red-500';
                                                    barColor = totalPct > 0 ? 'bg-red-400' : 'bg-red-200';
                                                }
                                            }
                                        }

                                        const barTextColor = barColor.includes('emerald') ? 'text-emerald-500' : barColor.includes('amber') ? 'text-amber-500' : barPct > 0 ? 'text-red-400' : 'text-red-300';

                                        return (
                                            <div key={team.team.id} className="flex items-center gap-3 px-4 py-3">
                                                <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
                                                <div className="w-8 h-8 p-0.5 bg-gray-50 rounded-xl border border-gray-100 shrink-0 flex items-center justify-center">
                                                    <ImageWithFallback src={getTeamLogo(team.team.id) || team.team.logo} alt="" className="w-full h-full object-contain" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <span className="text-xs font-black text-slate-800 truncate">
                                                            {getTeamName(team.team.id, lang, team.team.name)}
                                                        </span>
                                                        {getFifaRanking(team.team.id) !== null && (
                                                            <span className="shrink-0 text-[8px] font-black text-gray-400 bg-gray-100 px-1 py-0.5 rounded-md leading-none">
                                                                {formatFifaRank(getFifaRanking(team.team.id)!, lang)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className={`text-[10px] font-bold mt-0.5 ${statusTextColor}`}>{statusText}</div>
                                                </div>
                                                <div className="text-right shrink-0 mr-2">
                                                    <div className="text-sm font-black text-slate-900 tabular-nums">
                                                        {team.points}<span className="text-[9px] font-normal text-gray-400 ml-0.5">pts</span>
                                                    </div>
                                                    {!allDone && sc.maxPoints > team.points && (
                                                        <div className="text-[8px] text-gray-400">{isKo ? '최대' : 'max'} {sc.maxPoints}</div>
                                                    )}
                                                </div>
                                                <div className="w-12 shrink-0">
                                                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all ${barColor}`}
                                                            style={{ width: `${Math.max(barPct, barPct > 0 ? 4 : 0)}%` }}
                                                        />
                                                    </div>
                                                    <div className={`text-[8px] font-black text-center mt-0.5 ${barTextColor}`}>
                                                        {Math.round(barPct)}%
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    <div className="px-1 pb-2">
                        <p className="text-[9px] text-gray-400 text-center leading-relaxed">
                            {isKo
                                ? '* 승점 → 골득실 → 다득점 → 승자승 순으로 순위를 결정합니다. 조 3위 팀은 잔여 경기 기준 와일드카드 진출 확률을 표시합니다.'
                                : '* Ranked by: points → goal diff → goals scored → H2H. 3rd-place teams show wildcard probability based on remaining matches.'}
                        </p>
                    </div>
                </div>
            ) : (
                /* ──────── 조 3위 와일드카드 경쟁 테이블 ──────── */
                <div className="pb-10">
                    <div className="px-4 py-3 bg-slate-50 border-b border-gray-100">
                        <h3 className="text-[11px] font-black text-slate-600 uppercase tracking-widest">
                            {isKo ? '조 3위 와일드카드 경쟁' : '3rd Place Wildcard Race'}
                        </h3>
                        <p className="text-[9px] text-gray-400 mt-0.5">
                            {isKo ? '12개 조 3위팀 중 상위 8팀이 32강 진출' : 'Best 8 of 12 third-place teams advance to Round of 32'}
                        </p>
                    </div>

                    {/* 헤더 행 */}
                    <div className="grid grid-cols-12 gap-1 px-4 py-2.5 bg-white text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center sticky top-[53px] z-10">
                        <div className="col-span-1">#</div>
                        <div className="col-span-1">{isKo ? '조' : 'Grp'}</div>
                        <div className="col-span-4 text-left pl-2">{isKo ? '팀' : 'Team'}</div>
                        <div className="col-span-1">{isKo ? '경기' : 'P'}</div>
                        <div className="col-span-1">{isKo ? '승점' : 'Pts'}</div>
                        <div className="col-span-1">{isKo ? '득실' : 'GD'}</div>
                        <div className="col-span-1">{isKo ? '득점' : 'GF'}</div>
                        <div className="col-span-2 text-right pr-1">{isKo ? '진출' : 'Status'}</div>
                    </div>

                    {thirdPlaceTeams.map((entry, idx) => (
                        <div
                            key={entry.team.team.id}
                            className={`grid grid-cols-12 gap-1 items-center px-4 py-3.5 border-b transition-colors ${
                                idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                            } ${idx === 8 ? 'border-t-2 border-t-rose-100' : 'border-gray-50'}`}
                        >
                            <div className={`col-span-1 text-xs font-black text-center ${idx < 8 ? 'text-emerald-600' : 'text-red-400'}`}>
                                {idx + 1}
                            </div>
                            <div className="col-span-1 text-[10px] font-bold text-gray-500 text-center">
                                {entry.groupLetter}
                                {!entry.isComplete && (
                                    <span className="text-[7px] text-amber-400 ml-0.5 align-super">•</span>
                                )}
                            </div>
                            <div className="col-span-4 flex items-center gap-1.5 pl-1 min-w-0">
                                <div className="w-6 h-6 shrink-0 flex items-center justify-center">
                                    <ImageWithFallback src={getTeamLogo(entry.team.team.id) || entry.team.team.logo} alt="" className="w-full h-full object-contain" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-xs font-bold text-slate-800 truncate leading-tight">
                                        {getTeamName(entry.team.team.id, lang, entry.team.team.name)}
                                    </span>
                                    {getFifaRanking(entry.team.team.id) !== null && (
                                        <span className="text-[8px] font-black text-gray-400 leading-tight">
                                            {formatFifaRank(getFifaRanking(entry.team.team.id)!, lang)}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="col-span-1 text-[11px] font-medium text-slate-400 text-center">
                                {entry.team.played}
                            </div>
                            <div className="col-span-1 text-[11px] font-black text-slate-900 text-center">
                                {entry.team.points}
                            </div>
                            <div className={`col-span-1 text-[10px] font-black text-center ${
                                entry.team.goalDifference > 0 ? 'text-emerald-500' : entry.team.goalDifference < 0 ? 'text-rose-500' : 'text-gray-300'
                            }`}>
                                {entry.team.goalDifference > 0 ? `+${entry.team.goalDifference}` : entry.team.goalDifference}
                            </div>
                            <div className="col-span-1 text-[11px] font-medium text-slate-500 text-center">
                                {entry.team.goalsFor}
                            </div>
                            <div className="col-span-2 text-right pr-1">
                                {idx < 8 ? (
                                    <span className="text-[9px] font-black text-emerald-600">✓ {isKo ? '진출권' : 'Qualify'}</span>
                                ) : (
                                    <span className="text-[9px] font-black text-red-400">{isKo ? '탈락권' : 'Elim.'}</span>
                                )}
                            </div>
                        </div>
                    ))}

                    {thirdPlaceTeams.some(e => !e.isComplete) && (
                        <div className="px-4 py-3">
                            <p className="text-[9px] text-gray-400 text-center leading-relaxed">
                                {isKo
                                    ? '• 표시는 아직 경기가 남은 조입니다. 순위는 변경될 수 있습니다.'
                                    : '• indicates groups with remaining matches. Rankings may change.'}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
